import { Injectable, Injector } from '@angular/core';
import { lastValueFrom, Observable } from 'rxjs';
import { TableDatasource } from '@gamelearn/skeletor/models';
import { HttpDataService, ListHelperService } from 'core/services';
import { KeplerMatch, KeplerMatchList } from '../models/KeplerMatch';
import { HttpParams } from '@angular/common/http';

@Injectable()
export class KeplerService extends HttpDataService {
  constructor(protected injector: Injector, private listHelperService: ListHelperService) {
    super(injector, '/kepler/matches');
  }

  /**
   * Fetch of the kepler match list
   * @param clientId
   * @param options
   * @param dataSource
   */
  public fetch$(
    clientId: string,
    options?: {
      page: number;
      limit: number;
      sort: string;
      search: string;
      filters: any;
      aggregates: string;
      populate: string;
    },
    dataSource?: TableDatasource
  ): Observable<KeplerMatchList> {
    const url = `${this.baseUrl}`;
    const { status, ...$filterValues } = options.filters;
    const $filter = this.listHelperService.buildFilterString($filterValues, dataSource);
    const paginationOptions = {
      page: options.page,
      limit: options.limit,
      sort: options.sort,
      search: options.search,
      aggregates: options.aggregates,
      populate: options.populate,
      clientId,
      $status: status,
      $filter
    };

    return this.fetchRequest(url, paginationOptions);
  }

  public getKeplerMatch$(matchId: string): Observable<KeplerMatch> {
    return super.getItem(matchId);
  }

  public getMatchFederationRanking(matchId: string, options?) {
    return this.fetchRequest(`${this.baseUrl}/${matchId}/federations/ranking`, options);
  }

  public downloadFederationRanking(matchId: string, options?) {
    return this.fetchRequest(`${this.baseUrl}/${matchId}/federations/ranking/export`, options);
  }

  public getMatchRanking(matchId: string, options?) {
    return this.fetchRequest(`${this.baseUrl}/${matchId}/ranking`, options);
  }

  public downloadRanking(matchId: string, options?) {
    return this.fetchRequest(`${this.baseUrl}/${matchId}/ranking/export`, options);
  }

  public getRoundCsv(matchId: string, roundIndex) {
    return this.http.get(`${this.baseUrl}/${matchId}/round/${roundIndex}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public getRoundCsvTemplate(matchId: string, enabledLastRound?: boolean) {
    const userLang = localStorage.getItem('backofficelanguage');
    const queryLastRound = enabledLastRound ? 'isLastRound=true' : '';

    return this.http.get(`${this.baseUrl}/${matchId}/round-csv?lang=${userLang}&${queryLastRound}`, {
      observe: 'response',
      responseType: 'blob'
    });
  }

  public uploadKpisCsv(
    data: File,
    matchId: string,
    clientId: string,
    enabledLastRound?: boolean,
    objectivesDuration?: string
  ) {
    const formData: FormData = new FormData();
    formData.append('file', data);

    const fromObject: any = { clientId };
    if (enabledLastRound) fromObject.isLastRound = 'true';
    else fromObject.objectivesDuration = objectivesDuration;
    const params: HttpParams = new HttpParams({ fromObject });

    return lastValueFrom(this.http.post(`${this.baseUrl}/${matchId}/round`, formData, { params }));
  }

  public downloadMatchSupervisorsReport(matchId: string) {
    return this.http.get(`${this.baseUrl}/${matchId}/supervisors/report`, {
      observe: 'response',
      responseType: 'blob'
    });
  }
}
